import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link2, Loader2, RefreshCw, ChevronLeft, ChevronRight } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";

const API = process.env.REACT_APP_BACKEND_URL;
const PAGE = 50;

/** Admin → Εργαλεία: Σύνδεση παρεπόμενων δικογράφων με το κύριο (από SOLON «Αφορά Δικόγραφα»), προεπισκόπηση ανά 50 + μαζική σύνδεση. */
const DocLinksCard = () => {
  const [data, setData] = useState(null);
  const [skip, setSkip] = useState(0);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);

  const load = async (s = skip) => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/api/admin/doc-links/preview`, { params: { skip: s, limit: PAGE } });
      setData(r.data); setSkip(s);
      const next = {};
      (r.data.items || []).forEach((it) => { if (it.main_gak) next[it.case_id] = true; });
      setSel(next);
    } catch (e) { toast.error(e.response?.data?.detail || "Σφάλμα φόρτωσης"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(0); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const apply = async () => {
    const links = (data?.items || []).filter((it) => sel[it.case_id] && it.main_gak).map((it) => ({ case_id: it.case_id, main_gak: it.main_gak, main_year: it.main_year }));
    if (!links.length) return toast.error("Επιλέξτε δικόγραφα προς σύνδεση");
    if (!window.confirm(`Σύνδεση ${links.length} παρεπόμενων δικογράφων με το κύριο δικόγραφό τους;`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/admin/doc-links/apply`, { links });
      toast.success(`Συνδέθηκαν ${r.data.linked} δικόγραφα${r.data.with_main_in_db !== undefined ? ` (${r.data.with_main_in_db} με κύρια υπόθεση στη βάση)` : ""} — εμφανίζονται στην καρτέλα της κύριας υπόθεσης`); await load(0);
    } catch (e) { toast.error(e.response?.data?.detail || "Σφάλμα"); }
    finally { setBusy(false); }
  };

  const items = data?.items || [];
  const selected = items.filter((it) => sel[it.case_id]).length;
  return (
    <Card data-testid="doc-links-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2"><Link2 className="w-4 h-4 text-amber-600" />Σύνδεση Παρεπόμενων Δικογράφων με Κύριο Δικόγραφο</CardTitle>
        <CardDescription>Προσεπικλήσεις, πρόσθετες παρεμβάσεις, ανακοινώσεις δίκης κ.λπ. που έχουν ένδειξη «Αφορά Δικόγραφα» από το SOLON (ή από την εισαγωγή) συνδέονται μαζικά με την κύρια αγωγή/κλήση — ανά 50 με προεπισκόπηση. Οι συνδεδεμένες υποθέσεις δεν χρεώνονται ξανά στον δικαστή του κύριου δικογράφου.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2 text-xs text-slate-600 flex-wrap">
          <span data-testid="doc-links-total">Υποψήφια προς σύνδεση: <b>{data?.total ?? "…"}</b></span>
          <span>· εμφάνιση {items.length ? skip + 1 : 0}–{skip + items.length}</span>
          <Button size="sm" variant="ghost" onClick={() => load(skip)} disabled={busy} data-testid="doc-links-refresh"><RefreshCw className="w-4 h-4" /></Button>
          <div className="ml-auto flex items-center gap-1">
            <Button size="sm" variant="outline" disabled={busy || skip === 0} onClick={() => load(Math.max(0, skip - PAGE))} data-testid="doc-links-prev"><ChevronLeft className="w-4 h-4" /></Button>
            <Button size="sm" variant="outline" disabled={busy || skip + PAGE >= (data?.total || 0)} onClick={() => load(skip + PAGE)} data-testid="doc-links-next"><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>
        {items.length > 0 && (
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-xs" data-testid="doc-links-table">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="p-2 text-left"><input type="checkbox" checked={selected === items.filter((i) => i.main_gak).length && selected > 0}
                    onChange={(e) => { const n = {}; items.forEach((it) => { if (it.main_gak) n[it.case_id] = e.target.checked; }); setSel(n); }} data-testid="doc-links-select-all" /></th>
                  <th className="p-2 text-left">Παρεπόμενο δικόγραφο</th>
                  <th className="p-2 text-left">Είδος</th>
                  <th className="p-2 text-left">Αφορά (ΓΑΚ κύριου)</th>
                  <th className="p-2 text-left">Κύρια υπόθεση στο σύστημα</th>
                  <th className="p-2 text-left">Πηγή</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.case_id} className="border-t" data-testid={`doc-link-row-${it.case_id}`}>
                    <td className="p-2"><input type="checkbox" disabled={!it.main_gak} checked={!!sel[it.case_id]} onChange={(e) => setSel({ ...sel, [it.case_id]: e.target.checked })} data-testid={`doc-link-check-${it.case_id}`} /></td>
                    <td className="p-2 font-mono">{it.gak_number}/{it.gak_year}<div className="text-[10px] text-slate-400 font-sans">{it.pinakio || ""}</div></td>
                    <td className="p-2">{it.case_type || <span className="text-slate-400 italic">—</span>}</td>
                    <td className="p-2 font-mono">{it.main_gak ? `${it.main_gak}/${it.main_year}` : "—"}</td>
                    <td className="p-2">{it.main_case ? <span className="text-emerald-700">{it.main_case.case_type || "Αγωγή"} · {it.main_case.subject || "—"}{it.main_case.assigned_judge ? ` · Δικαστής: ${it.main_case.assigned_judge}` : ""}</span> : <span className="text-slate-400 italic">δεν βρέθηκε στη βάση — η σύνδεση αποθηκεύεται και εμφανίζεται μόλις εισαχθεί η κύρια</span>}</td>
                    <td className="p-2">{it.source === "solon" ? "SOLON" : "Εισαγωγή"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data && items.length === 0 && <p className="text-xs text-slate-500 italic">Δεν υπάρχουν παρεπόμενα δικόγραφα προς σύνδεση (χρειάζεται προηγούμενος έλεγχος SOLON με «Αφορά Δικόγραφα»).</p>}
        <Button size="sm" onClick={apply} disabled={busy || !selected} data-testid="doc-links-apply">
          {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Link2 className="w-4 h-4 mr-1" />}Μαζική σύνδεση ({selected})
        </Button>
      </CardContent>
    </Card>
  );
};

export default DocLinksCard;
